import React, { useCallback, useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarClock, Globe2, LifeBuoy, Loader2, MapPin, ShieldAlert, ShieldCheck, Sparkles, UserRound } from 'lucide-react';
import { type ProfilePublicDto, type SubscriptionAccessSnapshotDto, UpdateUserDto, type UserDto, UserTreatmentPreference } from '../api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import PublicLegalNavigation from '../components/PublicLegalNavigation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SupportContactLinks from '../components/SupportContactLinks';
import { subscribeToBirthRecordUpdated } from '../features/birthRecordRefresh';
import { formatCivilBirthDateTime } from '../helpers/birthDateTime';
import { getSubscriptionRefundRequestEligibility, type SubscriptionRefundRequestEligibility, type SubscriptionRefundRequestStatus } from '../features/subscriptionRefundRequests';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { usePublicLegalConfiguration } from '../platform/legalConfig';

type ProfileState = 'loaded' | 'unavailable';

type DetailRowProps = {
  label: string;
  value: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="border-t border-stone-100 py-3 sm:grid sm:grid-cols-[190px_minmax(0,1fr)] sm:gap-4">
    <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1 text-sm text-slate-800 sm:mt-0">{value}</div>
  </div>
);

const resolveTreatmentPreference = (value?: UserTreatmentPreference | null) =>
  value === UserTreatmentPreference.Feminine
    ? UserTreatmentPreference.Feminine
    : UserTreatmentPreference.Masculine;

const Account: React.FC = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const api = useAstroApiClient();
  const publicLegalConfiguration = usePublicLegalConfiguration();

  const [user, setUser] = useState<UserDto | null>(null);
  const [profile, setProfile] = useState<ProfilePublicDto | null>(null);
  const [profileState, setProfileState] = useState<ProfileState>('loaded');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refundEligibility, setRefundEligibility] = useState<SubscriptionRefundRequestEligibility | null>(null);
  const [subscriptionAccess, setSubscriptionAccess] = useState<SubscriptionAccessSnapshotDto | null>(null);
  const [selectedTreatmentPreference, setSelectedTreatmentPreference] = useState<UserTreatmentPreference>(UserTreatmentPreference.Masculine);
  const [isSavingTreatmentPreference, setIsSavingTreatmentPreference] = useState(false);
  const [treatmentPreferenceFeedback, setTreatmentPreferenceFeedback] = useState<string | null>(null);
  const [treatmentPreferenceError, setTreatmentPreferenceError] = useState<string | null>(null);

  const loadAccount = useCallback(async (activeRef?: { current: boolean }) => {
    setLoading(true);
    setLoadFailed(false);
    setSubscriptionAccess(null);

    try {
      const [account, access] = await Promise.all([
        api.apiUsersAuth(),
        api.apiSubscriptionAccess().catch(() => null),
      ]);
      if (activeRef && !activeRef.current) {
        return;
      }

      setUser(account);
      setSelectedTreatmentPreference(resolveTreatmentPreference(account?.treatmentPreference));
      setTreatmentPreferenceFeedback(null);
      setTreatmentPreferenceError(null);
      setSubscriptionAccess(access);

      if (!account?.id) {
        setProfile(null);
        setProfileState('unavailable');
        return;
      }

      try {
        const publicProfile = await api.apiSocialProfile(account.id);
        if (activeRef && !activeRef.current) {
          return;
        }

        setProfile(publicProfile);
        setProfileState('loaded');
      } catch {
        if (activeRef && !activeRef.current) {
          return;
        }

        setProfile(null);
        setProfileState('unavailable');
      }
    } catch {
      if (activeRef && !activeRef.current) {
        return;
      }

      setLoadFailed(true);
      setSubscriptionAccess(null);
    } finally {
      if (!activeRef || activeRef.current) {
        setLoading(false);
      }
    }
  }, [api]);

  useEffect(() => {
    const activeRef = { current: true };

    void loadAccount(activeRef);

    return () => {
      activeRef.current = false;
    };
  }, [loadAccount]);

  useEffect(() => {
    return subscribeToBirthRecordUpdated(() => {
      void loadAccount();
    });
  }, [loadAccount]);

  const loadRefundEligibility = useCallback(async (activeRef?: { current: boolean }) => {
    try {
      const result = await getSubscriptionRefundRequestEligibility(api);
      if (activeRef && !activeRef.current) {
        return;
      }

      setRefundEligibility(result);
    } catch {
      if (activeRef && !activeRef.current) {
        return;
      }

      setRefundEligibility(null);
    }
  }, [api]);

  useEffect(() => {
    const activeRef = { current: true };

    void loadRefundEligibility(activeRef);

    return () => {
      activeRef.current = false;
    };
  }, [loadRefundEligibility]);

  useEffect(() => {
    return subscribeToBirthRecordUpdated(() => {
      void loadRefundEligibility();
    });
  }, [loadRefundEligibility]);

  const formatDateTime = (value?: Date | string | null, timeZone?: string) => {
    if (!value) {
      return t('notAvailable');
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return t('notAvailable');
    }

    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone,
      }).format(parsed);
    } catch {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(parsed);
    }
  };

  const formatCoordinates = () => {
    if (typeof user?.latitude !== 'number' || typeof user?.longitude !== 'number') {
      return t('notAvailable');
    }

    return `${user.latitude.toFixed(6)}, ${user.longitude.toFixed(6)}`;
  };

  const formatCreditAmount = (value?: number | null) => {
    if (typeof value !== 'number') {
      return t('notAvailable');
    }

    return value.toLocaleString(i18n.language);
  };

  const formatTreatmentPreference = (value?: UserTreatmentPreference | null) =>
    resolveTreatmentPreference(value) === UserTreatmentPreference.Feminine
      ? t('treatmentPreferenceFeminine')
      : t('treatmentPreferenceMasculine');

  const handleSaveTreatmentPreference = async () => {
    if (!user || isSavingTreatmentPreference) {
      return;
    }

    setIsSavingTreatmentPreference(true);
    setTreatmentPreferenceFeedback(null);
    setTreatmentPreferenceError(null);

    try {
      const updatedUser = await api.apiUsersPut(new UpdateUserDto({
        name: user.name ?? '',
        language: user.language ?? 'pt',
        treatmentPreference: selectedTreatmentPreference,
      }));

      setUser(current => ({
        ...current,
        ...updatedUser,
        treatmentPreference: updatedUser.treatmentPreference ?? selectedTreatmentPreference,
      } as UserDto));
      setSelectedTreatmentPreference(resolveTreatmentPreference(updatedUser.treatmentPreference ?? selectedTreatmentPreference));
      setTreatmentPreferenceFeedback(t('accountTreatmentPreferenceSaved'));
    } catch {
      setTreatmentPreferenceError(t('accountTreatmentPreferenceError'));
    } finally {
      setIsSavingTreatmentPreference(false);
    }
  };

  const accountStatus = user?.isBanned
    ? user.bannedUntil
      ? t('accountStatusRestrictedUntil', { date: formatDateTime(user.bannedUntil, user.tzId) })
      : t('accountStatusRestricted')
    : t('accountStatusActive');
  const creditBalance = subscriptionAccess?.credits;
  const birthRecordUpdateCost = subscriptionAccess?.creditCosts?.find(cost => {
    const reason = cost.reason as unknown as string | null | undefined;
    return reason === 'UpdateBirthRecord';
  })?.amount;
  const birthRecordUpdateCostLabel =
    typeof birthRecordUpdateCost === 'number'
      ? t('accountBirthRecordUpdateCostBadge', {
          amount: formatCreditAmount(birthRecordUpdateCost),
        })
      : t('accountBirthRecordUpdateCostUnavailable');

  const getRefundRequestStatusLabel = (status?: SubscriptionRefundRequestStatus) => {
    if (status === 'Processed') return t('subscriptionRefundStatusProcessed');
    if (status === 'Rejected') return t('subscriptionRefundStatusRejected');
    return t('subscriptionRefundStatusPending');
  };

  const getRefundRequestStatusVariant = (status?: SubscriptionRefundRequestStatus) => {
    if (status === 'Processed') return 'secondary' as const;
    if (status === 'Rejected') return 'destructive' as const;
    return 'outline' as const;
  };

  const shouldShowRefundCard = refundEligibility && (refundEligibility.canSubmit || !!refundEligibility.existingRequest);
  const refundAccessNotice = publicLegalConfiguration.lifecycle.refundCancellationEndsProImmediately
    ? t('accountRefundAccessEndsImmediately')
    : t('accountRefundAccessFollowsPolicy');

  const safeAreaTop = 'var(--ion-safe-area-top, env(safe-area-inset-top))';
  const safeAreaBottom = 'var(--ion-safe-area-bottom, env(safe-area-inset-bottom))';

  return (
    <IonPage>
      <IonContent fullscreen className="bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100">
        <div
          className="min-h-screen"
          style={{
            minHeight: '100dvh',
            paddingBottom: `max(0.75rem, ${safeAreaBottom})`,
          }}
        >
          <div
            className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur-sm"
            style={{
              paddingTop: `max(0.5rem, ${safeAreaTop})`,
            }}
          >
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-action-700">
                  {t('myData')}
                </p>
                <h1 className="truncate text-lg font-semibold text-slate-900 md:text-2xl">
                  {t('accountTitle')}
                </h1>
                <p className="mt-1 hidden text-sm text-slate-600 sm:block">
                  {t('accountSubtitle')}
                </p>
              </div>
              <Button
                variant="outline"
                className="border-stone-300 bg-white/80 text-slate-700 hover:bg-stone-50"
                onClick={() => history.goBack()}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('goBack')}
              </Button>
            </div>
          </div>

          <main className="mx-auto max-w-5xl px-4 py-5 md:py-8">
            <div className="mb-4 rounded-2xl border border-action-200 bg-action-50/80 px-4 py-3 text-sm text-action-800 shadow-sm">
              {t('accountReadOnlyNotice')}
            </div>

            {loading ? (
              <Card className="border-stone-200 bg-white/90">
                <CardContent className="py-8 text-sm text-slate-600">{t('loading')}</CardContent>
              </Card>
            ) : loadFailed || !user ? (
              <Card className="border-red-200 bg-white/90">
                <CardContent className="py-8 text-sm text-red-600">{t('accountLoadError')}</CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-stone-200 bg-white/95 shadow-sm md:col-span-2">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <UserRound className="h-5 w-5 text-action-500" />
                      {t('accountIdentityTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountIdentityDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DetailRow label={t('name')} value={user.name || t('notAvailable')} />
                    <DetailRow label={t('accountPublicId')} value={user.publicId ? `@${user.publicId}` : t('notAvailable')} />
                    <DetailRow label={t('language')} value={user.language || t('notAvailable')} />
                    <DetailRow label={t('treatmentPreference')} value={formatTreatmentPreference(user.treatmentPreference)} />
                    <DetailRow label={t('timezone')} value={user.tzId || t('notAvailable')} />
                    <DetailRow label={t('accountRegistrationDate')} value={formatDateTime(user.createdAtUtc, user.tzId)} />
                    <DetailRow label={t('accountLastUpdated')} value={formatDateTime(user.updatedAtUtc, user.tzId)} />
                    <DetailRow label={t('accountStatus')} value={accountStatus} />
                    <div className="mt-4 border-t border-stone-100 pt-4">
                      <div className="mb-3 space-y-1">
                        <p className="text-sm font-semibold text-slate-900">
                          {t('accountTreatmentPreferenceTitle')}
                        </p>
                        <p className="text-xs leading-relaxed text-slate-600">
                          {t('accountTreatmentPreferenceDescription')}
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                        <div className="space-y-2">
                          <Label htmlFor="account-treatment-preference">{t('treatmentPreference')}</Label>
                          <Select
                            value={selectedTreatmentPreference}
                            onValueChange={(value: UserTreatmentPreference) => {
                              setSelectedTreatmentPreference(value);
                              setTreatmentPreferenceFeedback(null);
                              setTreatmentPreferenceError(null);
                            }}
                          >
                            <SelectTrigger
                              id="account-treatment-preference"
                              aria-label={t('treatmentPreference')}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value={UserTreatmentPreference.Masculine}>
                                {t('treatmentPreferenceMasculine')}
                              </SelectItem>
                              <SelectItem value={UserTreatmentPreference.Feminine}>
                                {t('treatmentPreferenceFeminine')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="border-stone-300 bg-white text-slate-700 hover:bg-stone-50"
                          disabled={
                            isSavingTreatmentPreference ||
                            selectedTreatmentPreference === resolveTreatmentPreference(user.treatmentPreference)
                          }
                          onClick={handleSaveTreatmentPreference}
                        >
                          {isSavingTreatmentPreference ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {isSavingTreatmentPreference
                            ? t('accountTreatmentPreferenceSaving')
                            : t('accountTreatmentPreferenceSave')}
                        </Button>
                      </div>
                      {treatmentPreferenceFeedback ? (
                        <p className="mt-3 text-sm text-emerald-700" aria-live="polite">
                          {treatmentPreferenceFeedback}
                        </p>
                      ) : null}
                      {treatmentPreferenceError ? (
                        <p className="mt-3 text-sm text-red-600" aria-live="polite">
                          {treatmentPreferenceError}
                        </p>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 bg-white/95 shadow-sm md:col-span-2">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <Sparkles className="h-5 w-5 text-emerald-500" />
                      {t('accountCreditsTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountCreditsDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DetailRow
                      label={t('accountCreditsAvailable')}
                      value={
                        typeof creditBalance?.remainingCredits === 'number' ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                            {formatCreditAmount(creditBalance.remainingCredits)}
                          </Badge>
                        ) : (
                          t('notAvailable')
                        )
                      }
                    />
                    <DetailRow label={t('accountCreditsIncluded')} value={formatCreditAmount(creditBalance?.includedCredits)} />
                    <DetailRow label={t('accountCreditsUsed')} value={formatCreditAmount(creditBalance?.usedCredits)} />
                    <DetailRow
                      label={t('accountCreditsRenewal')}
                      value={formatDateTime(creditBalance?.cycleEndsAtUtc ?? null, user.tzId)}
                    />
                  </CardContent>
                </Card>

                <Card className="border-stone-200 bg-white/95 shadow-sm">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <CalendarClock className="h-5 w-5 text-amber-500" />
                      {t('accountBirthRecordTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountBirthRecordDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DetailRow
                      label={t('birthDate')}
                      value={formatCivilBirthDateTime(user.birthLocalDate, user.birthLocalTime, i18n.language) ?? t('notAvailable')}
                    />
                    <DetailRow label={t('birthPlace')} value={user.birthPlace || t('notAvailable')} />
                    <DetailRow label={t('accountCoordinates')} value={formatCoordinates()} />
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-900">
                            {t('accountBirthRecordUpdateTitle')}
                          </p>
                          <p className="text-xs leading-relaxed text-slate-600">
                            {t('accountBirthRecordUpdateDescription')}
                          </p>
                        </div>
                        <Badge variant="outline" className="border-amber-300 bg-white text-amber-700">
                          {birthRecordUpdateCostLabel}
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-3 w-full border-amber-300 bg-white text-amber-700 sm:w-auto"
                        onClick={() => history.push('/account/birth-record')}
                      >
                        {t('accountBirthRecordUpdateAction')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 bg-white/95 shadow-sm">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <Globe2 className="h-5 w-5 text-sky-500" />
                      {t('accountPublicProfileTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountPublicProfileDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {profileState === 'loaded' && profile ? (
                      <>
                        <DetailRow label={t('accountDisplayName')} value={profile.displayName || t('notAvailable')} />
                        <DetailRow label={t('bio')} value={profile.bio || t('notAvailable')} />
                        <DetailRow
                          label={t('profileVisibility')}
                          value={profile.isPrivate ? t('private') : t('public')}
                        />
                      </>
                    ) : (
                      <div className="flex items-start gap-3 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-4 text-sm text-slate-600">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <span>{t('accountPublicProfileUnavailable')}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-sky-200 bg-white/95 shadow-sm md:col-span-2">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <LifeBuoy className="h-5 w-5 text-sky-500" />
                      {t('errorContactSupport')}
                    </CardTitle>
                    <CardDescription>{t('accountSupportDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <SupportContactLinks />
                    <p className="text-center text-xs leading-relaxed text-slate-600">
                      {t('errorSupportAvailability')}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 bg-white/95 shadow-sm md:col-span-2">
                  <CardHeader className="gap-3">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <ShieldCheck className="h-5 w-5 text-emerald-500" />
                      {t('legalNavigationTitle')}
                    </CardTitle>
                    <CardDescription>{t('accountLegalDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PublicLegalNavigation variant="section" />
                  </CardContent>
                </Card>

                {shouldShowRefundCard ? (
                  <Card className="border-amber-200 bg-white/95 shadow-sm md:col-span-2">
                    <CardHeader className="gap-3">
                      <CardTitle className="flex items-center gap-2 text-slate-900">
                        <ShieldAlert className="h-5 w-5 text-amber-500" />
                        {t('accountRefundCardTitle')}
                      </CardTitle>
                      <CardDescription>{t('accountRefundCardDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-2">
                        {refundEligibility?.existingRequest ? (
                          <>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant={getRefundRequestStatusVariant(refundEligibility.existingRequest.status)}>
                                {getRefundRequestStatusLabel(refundEligibility.existingRequest.status)}
                              </Badge>
                              <Badge variant="outline">
                                <LifeBuoy className="h-3 w-3" />
                                {refundEligibility.planName || t('subscriptionRefundPlanFallback')}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-700">
                              {t('accountRefundExistingRequestHint', {
                                date: formatDateTime(refundEligibility.existingRequest.createdAtUtc),
                              })}
                            </p>
                            <p className="text-sm text-slate-700">{refundAccessNotice}</p>
                          </>
                        ) : (
                          <>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary">{t('subscriptionRefundEligibleBadge')}</Badge>
                              <Badge variant="outline">
                                {refundEligibility?.planName || t('subscriptionRefundPlanFallback')}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-700">
                              {t('accountRefundEligibleHint', {
                                date: formatDateTime(refundEligibility?.eligibleUntilUtc),
                              })}
                            </p>
                            <p className="text-sm text-slate-700">{refundAccessNotice}</p>
                          </>
                        )}
                      </div>

                      <Button
                        type="button"
                        className="bg-slate-900 text-white hover:bg-slate-800"
                        onClick={() => history.push('/account/subscription-refund')}
                      >
                        {t('accountRefundOpenAction')}
                      </Button>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            )}
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Account;
